import { debounce } from 'lodash';
import { useMemo, useState, useCallback } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Stack,
  Checkbox,
  TextField,
  Typography,
  Autocomplete,
  AutocompleteRenderOptionState
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { DocumentType } from 'src/services/files/file.types';
import { ICandidateDocument } from 'src/services/candidates/candidates.types';
import {
  useGetCandidateDocumentsQuery
} from 'src/services/candidates/candidates.service';

import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

type DocumentSelectorProps = {
  type?: DocumentType | undefined;
  candidateId: string;
  label?: string;
  onSelect: (type: DocumentType, document: ICandidateDocument | null) => void;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function CandidateDocumentSelector({ type = undefined, onSelect, candidateId, label }: DocumentSelectorProps) {
  const { t } = useTranslate();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const showUploadModal = useBoolean();

  const { currentData: candidateDocuments, isLoading } = useGetCandidateDocumentsQuery(
    {
      candidate_id: candidateId as string,
      params: {
        page: 1,
        per_page: 5,
        search_query: searchQuery,
        type,
      },
    },
    { skip: !candidateId }
  );

  const [selectedDocument, setSelectedDocument] = useState<ICandidateDocument | null>(null);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: ICandidateDocument, { selected }: AutocompleteRenderOptionState) => (
    <li {...props}>
      <Stack direction="row">
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selectedDocument?.id === option.id}
          disabled={type === option.document.type}
        />
        <Stack direction="column" justifyContent="start" alignItems="start">
          <TruncatedTextField
            text={option.document.name}
            variant="body2"
            limit={50}
            mode='characters'
            showMoreEnabled={false}
          />

          <Typography variant="caption" color="text.secondary">
            {t(`enums.document_type.${option.document.type}`)}
          </Typography>
        </Stack>
      </Stack>
    </li>
  ), [type, t, selectedDocument]);

  return (
    <Stack direction="row" alignItems="center" gap={1} width="100%">

      <Autocomplete
        sx={{
          minWidth: 200,
          flexGrow: 1
        }}
        value={selectedDocument}
        onInputChange={(_e, value) => onSearchQueryChange(value)}
        onChange={(event, newValue) => {
          setSelectedDocument(newValue);
          onSelect(newValue?.document.type as DocumentType, newValue ?? null);
        }}
        options={candidateDocuments?.results ?? []}
        getOptionLabel={(option: ICandidateDocument) => option.document.name as string}
        getOptionDisabled={(option) => type !== option.document.type}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? t('applications.resource_requests.action.select_documents')}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off'
            }}
          />
        )}
      />

    </Stack>
  );
}